import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import FormDropdown from '../../../components/common/FormDropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import {
  umpireDetailsSchema,
  type UmpireDetailsFormValues,
} from '../validation/umpireDetailsSchema';
import { PAYMENT_FREQUENCY_OPTIONS } from '../constants/umpireDetailsConstants';
import './UmpireDetailsStep.scss';

const emptyUmpire: UmpireDetailsFormValues = {
  umpireLocationAvailability: '',
  experienceYears: '',
  feesPerHour: '',
  paymentFrequency: '',
};

interface UmpireDetailsStepProps {
  initialValues?: UmpireDetailsFormValues;
  onNext: (data: UmpireDetailsFormValues) => void;
  onCompleteLater: () => void | Promise<void>;
  partialSaving?: boolean;
}

function UmpireDetailsStep({
  initialValues,
  onNext,
  onCompleteLater,
  partialSaving = false,
}: UmpireDetailsStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<UmpireDetailsFormValues>({
    resolver: yupResolver(umpireDetailsSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues ?? emptyUmpire,
  });

  useEffect(() => {
    reset(initialValues ?? emptyUmpire);
  }, [initialValues, reset]);

  const onSubmit = async (values: UmpireDetailsFormValues) => {
    setIsSubmitting(true);
    try {
      onNext(values);
    } catch {
      toast.error('Failed to continue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="umpire-details-form">
      <div className="umpire-details-fields">
        <div className="umpire-details-field">
          <label htmlFor="umpireLocationAvailability">
            Umpire Location Availability <span className="req">*</span>
          </label>
          <input
            id="umpireLocationAvailability"
            type="text"
            placeholder="Enter location availability"
            className={errors.umpireLocationAvailability ? 'error' : ''}
            {...register('umpireLocationAvailability')}
          />
          {errors.umpireLocationAvailability && (
            <span className="field-error">
              {errors.umpireLocationAvailability.message}
            </span>
          )}
        </div>

        <div className="umpire-details-field">
          <label htmlFor="experienceYears">
            Experience (in years) <span className="req">*</span>
          </label>
          <input
            id="experienceYears"
            type="text"
            placeholder="Enter experience in years"
            className={errors.experienceYears ? 'error' : ''}
            {...register('experienceYears')}
          />
          {errors.experienceYears && (
            <span className="field-error">
              {errors.experienceYears.message}
            </span>
          )}
        </div>

        <div className="umpire-details-field">
          <label htmlFor="feesPerHour">
            Fees (per hour) <span className="req">*</span>
          </label>
          <input
            id="feesPerHour"
            type="text"
            placeholder="Enter fees per hour"
            className={errors.feesPerHour ? 'error' : ''}
            {...register('feesPerHour')}
          />
          {errors.feesPerHour && (
            <span className="field-error">{errors.feesPerHour.message}</span>
          )}
        </div>

        <div className="umpire-details-field">
          <label htmlFor="paymentFrequency">
            Payment Frequency <span className="req">*</span>
          </label>
          <Controller
            name="paymentFrequency"
            control={control}
            render={({ field }) => (
              <FormDropdown
                id="paymentFrequency"
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={PAYMENT_FREQUENCY_OPTIONS}
                hasError={!!errors.paymentFrequency}
              />
            )}
          />
          {errors.paymentFrequency && (
            <span className="field-error">
              {errors.paymentFrequency.message}
            </span>
          )}
        </div>
      </div>

      <div className="umpire-details-actions">
        <button
          type="button"
          className="btn-complete-later"
          onClick={() => void onCompleteLater()}
          disabled={partialSaving}
        >
          {partialSaving ? 'Saving…' : 'Complete Profile Later'}
        </button>
        <button type="submit" className="btn-next" disabled={isSubmitting || !isValid}>
          {isSubmitting ? 'Saving...' : 'Next'}
        </button>
      </div>
    </form>
  );
}

export default UmpireDetailsStep;
